package com.yendo.mx.ui.profile

import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.isSystemInDarkTheme
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.filled.*
import androidx.compose.material3.*
import kotlinx.coroutines.flow.catch
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.platform.LocalUriHandler
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.animation.animateColorAsState
import androidx.compose.animation.core.animateFloatAsState
import androidx.compose.ui.draw.scale
import com.yendo.mx.models.NotificacionFirebase
import com.yendo.mx.repository.AuthRepository
import com.yendo.mx.ui.theme.RosaYendo
import com.yendo.mx.utils.formatoFechaHora
import com.yendo.mx.utils.BackHandler
import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.firestore.Direction
import dev.gitlive.firebase.firestore.firestore
import kotlinx.coroutines.launch

// 🚀 IMPORTACIONES PARA IMÁGENES Y LOTTIE
import coil3.compose.AsyncImage
import coil3.compose.LocalPlatformContext
import coil3.request.ImageRequest
import coil3.request.crossfade
import org.jetbrains.compose.resources.ExperimentalResourceApi
import yendo.shared.generated.resources.*
import io.github.alexzhirkevich.compottie.Compottie
import io.github.alexzhirkevich.compottie.LottieCompositionSpec
import io.github.alexzhirkevich.compottie.rememberLottieComposition
import io.github.alexzhirkevich.compottie.rememberLottiePainter

@OptIn(ExperimentalMaterial3Api::class, ExperimentalResourceApi::class)
@Composable
fun NotificacionesScreen(
    onBack: () -> Unit,
    onAbrirPedido: (String) -> Unit, // Navega al PedidoActivoScreen
    onAbrirNegocio: (String) -> Unit // 🚀 NUEVO: Callback para abrir restaurante/producto
) {
    val isDark = isSystemInDarkTheme()
    val colorFondoBase = if (isDark) Color(0xFF121212) else Color(0xFFF8F8F8)
    val colorSuperficie = if (isDark) Color(0xFF1E1E1E) else Color.White
    val colorTextoPrimario = if (isDark) Color.White else Color.Black
    val colorTextoSecundario = if (isDark) Color(0xFFAAAAAA) else Color.Gray
    val colorDivisor = if (isDark) Color(0xFF2C2C2E) else Color(0xFFEEEEEE)

    val scope = rememberCoroutineScope()
    val uriHandler = LocalUriHandler.current
    val usuarioId = AuthRepository.usuarioActual.collectAsState().value?.uid

    val notificaciones = remember { mutableStateListOf<NotificacionFirebase>() }
    var isLoading by remember { mutableStateOf(true) }

    // ESTADO PARA LA VENTANA EMERGENTE
    var notificacionSeleccionada by remember { mutableStateOf<NotificacionFirebase?>(null) }

    // 🚀 INTERCEPTOR INTELIGENTE DEL BOTÓN ATRÁS
    BackHandler(enabled = true) {
        if (notificacionSeleccionada != null) {
            // 1. Si la alerta está abierta, solo la cerramos
            notificacionSeleccionada = null
        } else {
            // 2. Si no hay nada abierto, navegamos hacia atrás (al Home)
            onBack()
        }
    }

    // LOTTIE: Cargar la composición
    val composition by rememberLottieComposition {
        LottieCompositionSpec.JsonString(
            Res.readBytes("files/yendo_cargando.json").decodeToString()
        )
    }

    LaunchedEffect(usuarioId) {
        if (usuarioId != null) {
            try {
                Firebase.firestore
                    .collection("Usuarios")
                    .document(usuarioId)
                    .collection("Notificaciones")
                    .orderBy("fecha", Direction.DESCENDING)
                    .limit(40) // 🚀 AHORRO DE COSTOS: No traemos todo el historial de golpe
                    .snapshots
                    .catch { println("YENDO_ERROR: Error en flujo de notificaciones - ${it.message}"); isLoading = false }
                    .collect { snapshot ->
                        val lista = snapshot.documents.map { doc ->
                            doc.data<NotificacionFirebase>().copy(id = doc.id)
                        }
                        notificaciones.clear()
                        notificaciones.addAll(lista)
                        isLoading = false
                    }
            } catch (e: Exception) {
                println("YENDO_ERROR: Falló al cargar notificaciones - ${e.message}")
                isLoading = false
            }
        }
    }

    Scaffold(
        topBar = {
            TopAppBar(
                title = { Text("Notificaciones", fontWeight = FontWeight.ExtraBold, color = colorTextoPrimario) },
                navigationIcon = {
                    IconButton(onClick = onBack) { Icon(Icons.AutoMirrored.Filled.ArrowBack, "Atrás", tint = colorTextoPrimario) }
                },
                colors = TopAppBarDefaults.topAppBarColors(containerColor = colorSuperficie),
                actions = {
                    if (notificaciones.any { !it.leida }) {
                        IconButton(onClick = {
                            scope.launch {
                                val uid = usuarioId ?: return@launch
                                notificaciones.filter { !it.leida }.forEach { notif ->
                                    Firebase.firestore.collection("Usuarios").document(uid)
                                        .collection("Notificaciones").document(notif.id)
                                        .update("leida" to true)
                                }
                            }
                        }) {
                            Icon(Icons.Default.Drafts, contentDescription = "Marcar todas leídas", tint = RosaYendo)
                        }
                    }
                }
            )
        },
        containerColor = colorFondoBase
    ) { padding ->
        if (isLoading) {
            Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                val painter = rememberLottiePainter(
                    composition = composition,
                    iterations = Compottie.IterateForever
                )
                Image(
                    painter = painter,
                    contentDescription = "Cargando...",
                    modifier = Modifier.size(150.dp)
                )
            }
        } else if (notificaciones.isEmpty()) {
            Column(
                modifier = Modifier.fillMaxSize().padding(padding),
                horizontalAlignment = Alignment.CenterHorizontally,
                verticalArrangement = Arrangement.Center
            ) {
                Icon(Icons.Default.NotificationsOff, null, modifier = Modifier.size(80.dp), tint = colorTextoSecundario.copy(alpha = 0.5f))
                Spacer(Modifier.height(16.dp))
                Text("No tienes notificaciones", fontWeight = FontWeight.Bold, fontSize = 18.sp, color = colorTextoPrimario)
                Text("Aquí aparecerán tus avisos y promociones.", color = colorTextoSecundario, fontSize = 14.sp)
            }
        } else {
            LazyColumn(
                modifier = Modifier.fillMaxSize().padding(padding),
                contentPadding = PaddingValues(vertical = 8.dp)
            ) {
                items(notificaciones, key = { it.id }) { notificacion ->

                    val dismissState = rememberSwipeToDismissBoxState(
                        confirmValueChange = { dismissValue ->
                            if (dismissValue == SwipeToDismissBoxValue.EndToStart) {
                                scope.launch {
                                    val uid = usuarioId ?: return@launch
                                    Firebase.firestore.collection("Usuarios").document(uid)
                                        .collection("Notificaciones").document(notificacion.id)
                                        .delete()
                                }
                                true
                            } else false
                        }
                    )

                    // 🚀 ANIMACIONES PARA EL GESTO DE BORRADO
                    val isDismissing = dismissState.targetValue == SwipeToDismissBoxValue.EndToStart
                    
                    val scale by animateFloatAsState(
                        targetValue = if (isDismissing) 1.2f else 0.8f,
                        label = "escalaIconoBorrar"
                    )
                    
                    val bgColor by animateColorAsState(
                        targetValue = if (isDismissing) Color(0xFFF44336) else colorDivisor,
                        label = "colorFondoBorrar"
                    )

                    SwipeToDismissBox(
                        state = dismissState,
                        enableDismissFromStartToEnd = false,
                        backgroundContent = {
                            Box(
                                modifier = Modifier
                                    .fillMaxSize()
                                    .background(bgColor) // 🚀 Usa el color animado
                                    .padding(horizontal = 24.dp),
                                contentAlignment = Alignment.CenterEnd
                            ) {
                                Row(verticalAlignment = Alignment.CenterVertically) {
                                    if (isDismissing) {
                                        Text(
                                            text = "Eliminar", 
                                            color = Color.White, 
                                            fontWeight = FontWeight.Bold, 
                                            modifier = Modifier.padding(end = 12.dp)
                                        )
                                    }
                                    Icon(
                                        Icons.Default.Delete, 
                                        contentDescription = "Borrar", 
                                        tint = if (isDismissing) Color.White else colorTextoSecundario,
                                        modifier = Modifier.scale(scale) // 🚀 Usa la escala animada
                                    )
                                }
                            }
                        }
                    ) {
                        Surface(
                            modifier = Modifier.fillMaxWidth().clickable {
                                if (!notificacion.leida) {
                                    scope.launch {
                                        val uid = usuarioId ?: return@launch
                                        Firebase.firestore.collection("Usuarios").document(uid)
                                            .collection("Notificaciones").document(notificacion.id)
                                            .update("leida" to true)
                                    }
                                }

                                if (notificacion.tipo == "MENSAJE_CHAT" || notificacion.tipo == "PEDIDO") {
                                    if (notificacion.accion.isNotEmpty()) onAbrirPedido(notificacion.accion)
                                } else {
                                    notificacionSeleccionada = notificacion
                                }
                            },
                            color = if (notificacion.leida) colorFondoBase else colorSuperficie
                        ) {
                            Row(
                                modifier = Modifier.padding(16.dp).fillMaxWidth(),
                                verticalAlignment = Alignment.CenterVertically
                            ) {
                                Box(
                                    modifier = Modifier.size(48.dp).clip(CircleShape).background(if (notificacion.leida) colorDivisor else RosaYendo.copy(alpha = 0.1f)),
                                    contentAlignment = Alignment.Center
                                ) {
                                    val icono = when (notificacion.tipo) {
                                        "PEDIDO" -> Icons.Default.Receipt
                                        "PROMOCION" -> Icons.Default.LocalOffer
                                        "MENSAJE_CHAT" -> Icons.Default.ChatBubbleOutline // 🚀 ICONO DE CHAT
                                        else -> Icons.Default.Notifications
                                    }
                                    Icon(icono, null, tint = if (notificacion.leida) colorTextoSecundario else RosaYendo)
                                }

                                Spacer(Modifier.width(16.dp))

                                Column(modifier = Modifier.weight(1f)) {
                                    Text(
                                        text = notificacion.titulo,
                                        fontWeight = if (notificacion.leida) FontWeight.Normal else FontWeight.Bold,
                                        fontSize = 15.sp,
                                        color = colorTextoPrimario
                                    )
                                    Spacer(Modifier.height(4.dp))
                                    Text(
                                        text = notificacion.cuerpo,
                                        fontSize = 13.sp,
                                        color = colorTextoSecundario,
                                        maxLines = 2,
                                        overflow = TextOverflow.Ellipsis
                                    )
                                    Spacer(Modifier.height(6.dp))
                                    Text(
                                        text = formatoFechaHora(notificacion.fecha),
                                        fontSize = 11.sp,
                                        color = colorTextoSecundario.copy(alpha = 0.7f)
                                    )
                                }

                                if (!notificacion.leida) {
                                    Spacer(Modifier.width(8.dp))
                                    Box(modifier = Modifier.size(10.dp).clip(CircleShape).background(RosaYendo))
                                }
                            }
                        }
                    }
                    HorizontalDivider(color = colorDivisor)
                }
            }
        }

        // 🚀 VENTANA EMERGENTE (DIALOG) CON SOPORTE PARA IMAGEN
        notificacionSeleccionada?.let { notif ->
            AlertDialog(
                onDismissRequest = { notificacionSeleccionada = null },
                containerColor = colorSuperficie,
                title = {
                    Row(verticalAlignment = Alignment.CenterVertically) {
                        val icono = when (notif.tipo) {
                            "PROMOCION" -> Icons.Default.LocalOffer
                            else -> Icons.Default.Notifications
                        }
                        Icon(icono, null, tint = RosaYendo, modifier = Modifier.size(24.dp))
                        Spacer(Modifier.width(8.dp))
                        Text(notif.titulo, fontWeight = FontWeight.Bold, fontSize = 20.sp, color = colorTextoPrimario)
                    }
                },
                text = {
                    Column(modifier = Modifier.verticalScroll(rememberScrollState())) {
                        Text(
                            text = formatoFechaHora(notif.fecha),
                            fontSize = 12.sp,
                            color = colorTextoSecundario,
                            modifier = Modifier.padding(bottom = 16.dp)
                        )

                        // 📸 LÓGICA DE INYECCIÓN DE IMAGEN ROBUSTA
                        if (!notif.imagen.isNullOrEmpty()) {
                            AsyncImage(
                                model = ImageRequest.Builder(LocalPlatformContext.current)
                                    .data(notif.imagen)
                                    .crossfade(true)
                                    .build(),
                                contentDescription = "Imagen de la notificación",
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .height(200.dp)
                                    .padding(bottom = 16.dp)
                                    .clip(RoundedCornerShape(12.dp))
                                    .background(colorDivisor), // 🚀 Fondo gris "esqueleto" mientras descarga
                                contentScale = ContentScale.Crop
                            )
                        }

                        Text(
                            text = notif.cuerpo,
                            fontSize = 15.sp,
                            color = colorTextoPrimario,
                            lineHeight = 22.sp
                        )
                    }
                },
                confirmButton = {
                    Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.End) {
                        if (notif.accion.isNotBlank()) {
                            TextButton(onClick = { notificacionSeleccionada = null }) {
                                Text("Cerrar", color = colorTextoSecundario)
                            }
                            Spacer(Modifier.width(8.dp))
                            Button(
                                onClick = { 
                                    // 🚀 LÓGICA INTELIGENTE DE REDIRECCIÓN MÚLTIPLE
                                    if (notif.tipo == "MENSAJE_CHAT" || notif.tipo == "PEDIDO") {
                                        onAbrirPedido(notif.accion)
                                    } else if (notif.accion.startsWith("http", ignoreCase = true)) {
                                        try { uriHandler.openUri(notif.accion) } catch(e: Exception){}
                                    } else {
                                        // Si no es chat ni web, asumimos que es el perfil de un negocio
                                        onAbrirNegocio(notif.accion)
                                    }
                                    notificacionSeleccionada = null 
                                },
                                colors = ButtonDefaults.buttonColors(containerColor = RosaYendo),
                                shape = RoundedCornerShape(12.dp)
                            ) {
                                Text("Ver más", fontWeight = FontWeight.Bold, color = Color.White)
                            }
                        } else {
                            Button(
                                onClick = { notificacionSeleccionada = null },
                                colors = ButtonDefaults.buttonColors(containerColor = RosaYendo),
                                shape = RoundedCornerShape(12.dp)
                            ) {
                                Text("Entendido", fontWeight = FontWeight.Bold, color = Color.White)
                            }
                        }
                    }
                }
            )
        }
    }
}